import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    usePerformBoOperationMutation,
    type PerformBoOperationVariables,
} from "@src/businessModules/WorkShop/shared/bo/usePerformBoOperationMutation";
import {
    storePlacesServices,
    type StorePlaceCancelPlacementParams,
    type StorePlacePlaceLotParams,
} from "../../../../api/storePlaces";

/** Параметры размещения партии с кодом строки состава для точечного refetch. */
export type StockPlacementPlaceLotVariables = StorePlacePlaceLotParams & {
    /** Код строки состава ПО для обновления в cache. */
    contentCode: number;
};

/** Параметры отмены размещения с кодом строки состава для точечного refetch. */
export type StockPlacementCancelPlacementVariables = StorePlaceCancelPlacementParams & {
    /** Код строки состава ПО для обновления в cache. */
    contentCode: number;
};

/** Параметры хука мутаций карточки размещения ТМЦ. */
export interface UseStockPlacementCardMutationsParams {
    /**
     * Колбэк синхронизации обновлённой строки состава после place/cancel.
     * @param contentCode код строки состава ПО
     */
    onContentRefetch?: (contentCode: number) => void | Promise<unknown>;
    /** Колбэк успешной BO-операции. */
    onBoOperationSuccess?: () => void;
}

/** Мутации карточки размещения ТМЦ. */
export interface UseStockPlacementCardMutationsResult {
    /** Закрепление партии в ячейке. */
    placeLotMutation: UseMutationResult<void, Error, StockPlacementPlaceLotVariables>;
    /** Отмена ошибочного размещения партии. */
    cancelPlacementMutation: UseMutationResult<void, Error, StockPlacementCancelPlacementVariables>;
    /** Выполнение BO-операции над приходным ордером. */
    performBoOperationMutation: UseMutationResult<void, Error, PerformBoOperationVariables>;
}

/** Screen-level мутации карточки размещения ТМЦ. */
export function useStockPlacementCardMutations({
    onContentRefetch,
    onBoOperationSuccess,
}: UseStockPlacementCardMutationsParams): UseStockPlacementCardMutationsResult {
    const placeLotMutation = useMutation({
        mutationKey: ["stock", "placement", "placeLot"],
        meta: { errorSource: "stock.placement.placeLot" },
        mutationFn: ({ contentCode: _contentCode, ...variables }: StockPlacementPlaceLotVariables) =>
            storePlacesServices.placeLot(variables),
        onSuccess: async (_data, variables) => {
            notification.success("Размещение сохранено");
            await onContentRefetch?.(variables.contentCode);
        },
    });

    const cancelPlacementMutation = useMutation({
        mutationKey: ["stock", "placement", "cancelPlacement"],
        meta: { errorSource: "stock.placement.cancelPlacement" },
        mutationFn: ({ contentCode: _contentCode, ...variables }: StockPlacementCancelPlacementVariables) =>
            storePlacesServices.cancelPlacement(variables),
        onSuccess: async (_data, variables) => {
            notification.success("Размещение отменено");
            await onContentRefetch?.(variables.contentCode);
        },
    });

    const performBoOperationMutation = usePerformBoOperationMutation({
        scope: "stockPlacement",
        onSuccess: onBoOperationSuccess,
    });

    return {
        placeLotMutation,
        cancelPlacementMutation,
        performBoOperationMutation,
    };
}
